import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty, get, set, cloneDeep, isNil, omit, forEach } from 'lodash';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';

// local dependencies
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as ROUTE_PATHS from 'constants/route-paths';
import * as sessionManager from 'utils/sessionManager';
import { REDUCER } from 'constants/entity-names';
import { SERVICE_RESPONSE_STATUS, GLOBAL_ERRORS, SIZES, GLOBAL_LABELS } from 'constants/common';
import { GetAppContextService } from 'services/getAppContext';
import { updateAppContext } from 'store/actions/updateAppContext';
import { initCallState } from 'store/actions/callState';
import { heartBeatPreloader as HeartBeatPreloader } from 'ui/views/generic/heartBeatPreloader';
import * as errorOverlay from 'utils/errorOverlay';
import { getLabel } from 'utils/getLabel';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as successOverlay from 'utils/successOverlay';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import { shouldTriggerMessageBus } from 'resolvers/shouldTriggerMessageBus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import shortid from 'shortid';

/**
 * @type {function}
 * @desc This component fetches the application-wide context information and stores it in the Redux store.
 * @param {object} Component - A React component
 * @example
 * import { checkAppContext, CheckAppContext } from 'ui/hoc/checkAppContext';
 * import { testView as TestView } from 'views/Test.view';
 *
 * // create a component
 * const ComponentWithHOC = CheckAppContext( TestView );
 * @return {class} - A standard React component
 */
const checkAppContext = ( Component ) => {
    class CheckAppContext extends React.Component {
        constructor( props ) {
			log.debug( 'CheckAppContextHoc.constructor()' );

			super( props );

			// bind methods
			this.submitEpostId = this.submitEpostId.bind( this );

			// save network response meta data
            this.data = {
                responseMetaData: {}
			};

			// component state
			this.state = {
				isNetworkResponseOk: true,
				isResponseValid: true,
				socket: null
			};

			// app context service
			this.getAppContextService = new GetAppContextService();

            // load app conext information if not present in the store
            if( isEmpty( props.appContext ) ) {
                this.loadAppContext();
            } else {
				this.establishWebsocketConnection( {
					webSocketUrl: get( props.appContext, 'component.data.state.globalUrls.webSocketUrl', '' ),
					authorization: get( props.appContext, 'component.data.state.authorization', {} )
				} );
			}
		}

		/**
		 * @desc Fetch and cache application context information.
		 */
        async loadAppContext() {
			log.debug( 'CheckAppContextHoc.loadAppContext()' );

			// cancel ongoing request
			this.getAppContextService.cancel();

            // show page level preloader
			pageLevelPreloader.show();

			try {
				const response = await this.getAppContextService.postData();

				// save network response parameters
				const { body } = response;
				const clientConfig = get( response, 'body.component.data.state.clientConfiguration', {} );
				// check if response status was success
				if( SERVICE_RESPONSE_STATUS.SUCCESS !== body.status ) {

					// set flag `isResponseValid` to `false`
					this.setState( {
						isResponseValid: false
					} );
				
				} else if( true === clientConfig )  {
					return this.openErrorEpostDataForm( body );
				} else {

					// set user logged in status in localstorage
					sessionManager.setUserLoggedIn();

					// update user info into the store
					this.props.updateAppContext( body );

					// capture epost id
					if ( isEmpty( get( body, 'component.data.state.authorization.ePostId' ) ) ) {
						this.openAddEpostIdForm( body );
					} else {

						// epost id available; establish a web socket connection
						this.establishWebsocketConnection( {
							webSocketUrl: get( body, 'component.data.state.globalUrls.webSocketUrl', '' ),
							authorization: get( body, 'component.data.state.authorization', {} )
						} );
					}

					// display global error message
					this.showGlobalErrorMessage();

					// initialize call state in redux store
					if( ! isEmpty( get( body, 'component.data.state.callData' ) ) ) {
						this.props.initCallState( get( body, 'component.data.state.callData', {} ) );
					}
				}

			} catch( e ) {

				// set flag `isNetworkResponseOk` to `false` and show error dialog
				this.setState( {
					isNetworkResponseOk: false
				} );
			} finally {

				// hide page level preloader
				pageLevelPreloader.hide();
			}
		}

		/**
		 * @desc Display error message if `error` query param exists.
		 */
		showGlobalErrorMessage() {
			log.debug( 'CheckAppContextHoc.showGlobalErrorMessage()' );

			const queryParams = queryString.parse( this.props.location.search );
			const errorType = get( queryParams, 'error', null );

			// get the error Message from the url
			const errorMsg = get( queryParams, 'errorMsg', null );

			// if error is not present, return
			if( isEmpty( errorType ) ) {
				return;
			}

			// display error message based on the error type
			switch( errorType ) {

				// payment: card already exists
				case GLOBAL_ERRORS.PAYMENT_CARD_EXISTS: {
					const errorMessage = ! isNil( errorMsg ) ? errorMsg: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_PAYMENT_ERROR_CARD_EXISTS );
					window.history.replaceState( null, null, this.props.location.pathname +'?'+ queryString.stringify( omit( queryParams, [ 'error', 'errorMsg' ] ) ) );
					return errorOverlay.open( { message: errorMessage, size: SIZES.SMALL } );
				}

				// payment: general error
				case GLOBAL_ERRORS.PAYMENT_GENERAL: {
					const errorMessage = ! isNil( errorMsg ) ? errorMsg: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_PAYMENT_ERROR_GENERAL );
					window.history.replaceState( null, null, this.props.location.pathname +'?'+ queryString.stringify( omit( queryParams, [ 'error', 'errorMsg' ] ) ) );
					return errorOverlay.open( { message: errorMessage, size: SIZES.SMALL } );
				}

				// payment: general error
				case GLOBAL_ERRORS.CARD_TYPE_NOT_FOUND: {
					const errorMessage = ! isNil( errorMsg ) ? errorMsg: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_ERROR_CARD_TYPE_NOT_FOUND );
					window.history.replaceState( null, null, this.props.location.pathname +'?'+ queryString.stringify( omit( queryParams, [ 'error', 'errorMsg' ] ) ) );
					return errorOverlay.open( { message: errorMessage, size: SIZES.SMALL } );
				}

				// else: no-op
				default: {
					return;
				}
			}

		}

		/*******************************************************************/

		// open add ePost ID form
		openAddEpostIdForm( response = {} ) {
			log.debug( 'CheckAppContextHoc.openAddEpostIdForm()' );

			overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.SUBMIT_AUTH_EPOST_ID,
                    showCloseButton: false,
                    ignoreOutsideClick: true,
                },
                config: {},
                data: {
					mode: 'add',
                    title: get( response, 'component.structure.authorization.title', {} ),
                    fields: get( response, 'component.structure.authorization.fields', [] ),
					actions: get( response, 'component.structure.authorization.actions', [] ),
					webSocketConfig: {
						webSocketUrl: get( response, 'component.data.state.globalUrls.webSocketUrl', '' ),
						authorization: get( response, 'component.data.state.authorization', {} )
					},
					values: {},
					onSubmit: this.submitEpostId,
                },
            } );
		}

		//Epost Error
		openErrorEpostDataForm( response = {} ) {
			log.debug( 'CheckAppContextHoc.openErrorEpostDataForm()' );

			overlay.open( {
				settings: {
					modalType: OVERLAY_VIEWS.ERROR_VIEW ,
					showCloseButton: false,
					ignoreOutsideClick: false,
					size: 'small',
					templateType: ''
					
				},
				config: {},
				data: {
					title: get( response, 'component.elements.labels.ClientConfigErrorTitle', {} ),
					values: get( response, 'component.elements.labels.ClientConfigErrorDescription', {} ),
					message: get( response, 'component.elements.labels.ClientConfigErrorDescription', {} ),
					size: 'small',
					templateType: ''
					
				},
			} );
			
		}


		// submit ePost ID
		async submitEpostId( { values, url, webSocketConfig } = {} ) {
			log.debug( 'CheckAppContextHoc.submitEpostId()' );

			// get `ePostId` from form values
			const ePostId = get( values, 'ePostId', null );

			// cancel ongoing request
			this.getAppContextService.cancel();

			// show page level preloader
			pageLevelPreloader.show();
	
			// get status and service data
			const { status, data } = await this.getAppContextService.submitEpostId( {
				url: url,
				data: {
					data: {
						ePostId: ePostId,
					}
				}
			} );
	
			// show page level preloader
			pageLevelPreloader.hide();
	
			// on success, save `ePostId` in redux store
			if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

				// close overlay form
				overlay.close();

				// update `appContext` in redux store
				const appContext = cloneDeep( this.props.appContext );
				set( appContext, 'component.data.state.authorization.ePostId', ePostId );
				this.props.updateAppContext( appContext );

				// establish a web socket connection
				this.establishWebsocketConnection( webSocketConfig );

				// display success message
				successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
			}  else {
				
				// show error dialog
				if ( ! isEmpty( data.description ) ) {
					errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
				} else {
					errorOverlay.open();
				}
			}
		}

		/**
		 * Handles establishment of WebSocket and its listeners
		 * @param {object} webSocketConfig - An object that contains webSocketUrl and authorization data
		 */
		establishWebsocketConnection = ( { webSocketUrl, authorization } ) => {
			log.debug( 'CheckAppContextHoc.establishWebsocketConnection()' );

			try{ //adding try to catch any error's when creating connection

				if ( ! isEmpty( webSocketUrl ) ) {

					const socket = new WebSocket( webSocketUrl );
					const closeWebsocketOnRefresh = () => { socket.close(); };

					socket.onopen = () => {
						this.setState( { socket }, () => {
							log.debug( 'CONNECTED!' );
							socket.send( authorization.agentId );
							window.addEventListener( 'beforeunload', closeWebsocketOnRefresh );
						} );
					};
			
					socket.onmessage = ( event ) => {
						const payload = JSON.parse( event.data );
						const status = get( payload, 'status', SERVICE_RESPONSE_STATUS.FAILURE );
						const context = get( payload, 'message.context', {} );
						
						if ( shouldTriggerMessageBus( { status, context, props: this.props } ) ) {
							const requestData = get( payload, 'message.data', {} );
							const widgets = get( payload, 'message.structure.widgets', [] );

							forEach( widgets, widget => {
								const widgetName = widget.name;
								const requestType = widget.type;
								const id = shortid.generate();

								// trigger for flyout
								MessageBus.trigger( `${ MESSAGE_BUS_EVENTS.ADD_TO_FLYOUT }`, {
									id,
									widgetName,
									requestType
								} );

								// trigger for widget
								MessageBus.trigger( `${ widgetName }_${ requestType }`, {  // e.g. memberDetailsAlertsStats_RELOAD
									id,
									widgetName,
									requestType,
									context,
									requestData,
									itemOperation: requestData.itemOperation,
								} );
							} );
						}
					};

					socket.onerror = () => {
						socket.close();
					};

					socket.onclose = () => {
						this.setState( { socket: null }, () => {
							window.removeEventListener( 'beforeunload', closeWebsocketOnRefresh );
							log.debug( 'CLOSED. RETRYING...' );
							this.establishWebsocketConnection( { webSocketUrl, authorization } );
						} );
					};
				}

			} catch( e ) {
				log.error( e );
			}
		}

		/*******************************************************************/

		/**
         * @desc When component unmounts, remove bindings, cancel AJAX requests and close websocket
         */
        componentWillUnmount() {
			log.debug( 'CheckAppContextHoc.componentWillUnmount()' );
			
			// cancel ongoing request
			this.getAppContextService.cancel();
        }

		/**
         * @desc Render different views based on whether user information is present in store.
         * If user information is missing, AJAX request will populate in the store.
         */
        render() {

			/**
             * If appContext is present in store, validate and render component.
             * If not, then redirect to error message screen based on error flag.
             */
			if( ! isEmpty( this.props.appContext ) ) {

				// do not render route unless authorization is done
				if ( isEmpty( get( this.props.appContext, 'component.data.state.authorization.ePostId' ) ) ) {
					return null;
				}

				// render route component
				return (
					<Component { ...this.props } />
				);
            } else if( ! this.state.isResponseValid ) {

				// network response not valid, redirect to custom error page
                return (
                    <Redirect to={ { pathname: ROUTE_PATHS.UNAUTHENTICATED } } />
				);

            } else if( ! this.state.isNetworkResponseOk ) {

                // network response not ok, return null view
				return null;

            } else {
                return (
					<HeartBeatPreloader/>
				);
			}
        }
    }

    // set display name
    CheckAppContext.displayName = 'CheckAppContext';

    // set default props
    CheckAppContext.defaultProps = {};

    /*************************************************/

    // map state to props
    const mapStateToProps = ( state ) => {
        return {
            appContext: state[ REDUCER.APP_CONTEXT ]
        };
    };

    // map dispatch to props
    const mapDispatchToProps = ( dispatch ) => {
        return {
			updateAppContext: bindActionCreators( updateAppContext, dispatch ),
			initCallState: bindActionCreators( initCallState, dispatch ),
        };
    };


    // return component with acccess to Redux store
    return connect( mapStateToProps, mapDispatchToProps )( CheckAppContext );
};

export { checkAppContext };
